import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FiCheckCircle, FiLoader, FiMail, FiMessageSquare } from "react-icons/fi";
import axios from "../api/axiosInstance";

function normalizeText(value) {
  return String(value || "").trim();
}

function buildScaleRange(question) {
  const type = String(question?.question_type || "").toLowerCase();
  const min = type === "nps" ? 0 : Number(question?.options_json?.scale_min || 1);
  const max = type === "nps" ? 10 : Number(question?.options_json?.scale_max || 5);
  return Array.from({ length: Math.max(0, max - min + 1) }, (_, index) => min + index);
}

function GuestFeedbackSurveyPublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [error, setError] = useState("");
  const [state, setState] = useState({ mode: "loading", thankYouMessage: "" });

  useEffect(() => {
    let cancelled = false;

    const loadSurvey = async () => {
      setLoading(true);
      setError("");
      setState({ mode: "loading", thankYouMessage: "" });

      try {
        const response = await axios.get(`/api/reviews-intelligence/surveys/public/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setSurvey(response.data || null);
        setGuestName(response.data?.sendout?.guest_name || "");
        setGuestEmail(response.data?.sendout?.guest_email || "");
        setState({ mode: "ready", thankYouMessage: "" });
      } catch (requestError) {
        if (cancelled) return;
        const message = requestError?.response?.data?.error || "We could not load this survey.";
        const thankYouMessage = requestError?.response?.data?.thank_you_message || "";
        const status = requestError?.response?.status;
        setError(message);
        if (status === 409) {
          setState({ mode: "answered", thankYouMessage });
        } else if (status === 410) {
          setState({ mode: "expired", thankYouMessage });
        } else {
          setState({ mode: "error", thankYouMessage: "" });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSurvey();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const questions = Array.isArray(survey?.template?.questions) ? survey.template.questions : [];

  const missingRequiredQuestions = useMemo(
    () =>
      questions.filter((question) => {
        if (!question.is_required) return false;
        const currentValue = answers[question.id];
        if (Array.isArray(currentValue)) return currentValue.length === 0;
        return currentValue === undefined || currentValue === null || currentValue === "";
      }),
    [answers, questions]
  );

  const updateAnswer = (questionId, value) => {
    setAnswers((current) => ({ ...current, [questionId]: value }));
  };

  const toggleMultiChoice = (questionId, option) => {
    setAnswers((current) => {
      const existing = Array.isArray(current[questionId]) ? current[questionId] : [];
      return {
        ...current,
        [questionId]: existing.includes(option)
          ? existing.filter((item) => item !== option)
          : [...existing, option],
      };
    });
  };

  const submitSurvey = async (event) => {
    event.preventDefault();
    if (missingRequiredQuestions.length) {
      setError("Please answer all required questions before sending the survey.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const response = await axios.post(`/api/reviews-intelligence/surveys/public/${encodeURIComponent(token)}/submit`, {
        guest_name: guestName || null,
        guest_email: guestEmail || null,
        answers,
      });
      setState({
        mode: "submitted",
        thankYouMessage:
          response.data?.thank_you_message ||
          "Thank you for sharing your feedback with the hotel.",
      });
    } catch (requestError) {
      const message = requestError?.response?.data?.error || "We could not submit your answers.";
      const thankYouMessage = requestError?.response?.data?.thank_you_message || "";
      const status = requestError?.response?.status;
      setError(message);
      if (status === 409) {
        setState({ mode: "answered", thankYouMessage });
      } else if (status === 410) {
        setState({ mode: "expired", thankYouMessage });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const styles = `
    @keyframes gsp-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    .gsp-shell{min-height:100vh;padding:24px;background:radial-gradient(1200px 520px at 10% -10%,rgba(48,216,255,.10),transparent 58%),radial-gradient(900px 520px at 90% 0%,rgba(168,85,247,.14),transparent 58%),linear-gradient(180deg,#0c1020 0%,#080b16 100%);display:flex;align-items:center;justify-content:center}
    .gsp-card{width:min(920px,100%);border-radius:28px;border:1px solid rgba(255,255,255,.10);background:linear-gradient(180deg,rgba(24,24,48,.96),rgba(10,10,22,.98));box-shadow:0 32px 80px rgba(8,12,26,.45);color:rgba(240,246,255,.96);overflow:hidden}
    .gsp-header{padding:28px 30px 22px;border-bottom:1px solid rgba(255,255,255,.08)}
    .gsp-title{margin:0;font-size:1.7rem;font-weight:800;letter-spacing:-.03em}
    .gsp-subtitle{margin:10px 0 0;font-size:.95rem;line-height:1.65;color:rgba(214,226,247,.72)}
    .gsp-body{padding:24px 30px 30px;display:flex;flex-direction:column;gap:18px}
    .gsp-banner{padding:16px 18px;border-radius:18px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);font-size:.9rem;line-height:1.65;color:rgba(226,236,249,.88)}
    .gsp-banner--success{background:rgba(25,164,122,.16);border-color:rgba(25,164,122,.28);color:rgba(182,245,223,.98)}
    .gsp-banner--error{background:rgba(177,48,69,.22);border-color:rgba(255,125,147,.30);color:rgba(255,214,214,.98)}
    .gsp-form{display:flex;flex-direction:column;gap:18px}
    .gsp-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
    .gsp-field{display:flex;flex-direction:column;gap:8px}
    .gsp-label{font-size:.82rem;font-weight:700;color:rgba(244,248,255,.96)}
    .gsp-input,.gsp-textarea{width:100%;border-radius:14px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:rgba(246,249,255,.97);font-size:.95rem}
    .gsp-input{height:46px;padding:0 14px}
    .gsp-textarea{min-height:120px;padding:14px;line-height:1.6;resize:vertical}
    .gsp-question{padding:18px;border-radius:20px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03)}
    .gsp-questionTitle{font-size:1rem;font-weight:700;color:rgba(247,250,255,.98)}
    .gsp-required{margin-left:6px;font-size:.75rem;color:rgba(255,189,189,.92)}
    .gsp-questionHelp{margin-top:6px;font-size:.8rem;line-height:1.55;color:rgba(214,226,247,.64)}
    .gsp-scale,.gsp-choiceList{display:flex;gap:10px;flex-wrap:wrap;margin-top:14px}
    .gsp-scaleBtn,.gsp-choiceBtn{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:rgba(244,248,255,.94);border-radius:14px;padding:10px 14px;font-weight:700;font-size:.92rem;min-width:48px}
    .gsp-scaleBtn--active,.gsp-choiceBtn--active{background:linear-gradient(135deg,rgba(48,216,255,.18),rgba(139,92,246,.30));border-color:rgba(109,184,255,.34)}
    .gsp-choiceBtn{min-width:auto}
    .gsp-checkList{display:flex;flex-direction:column;gap:10px;margin-top:14px}
    .gsp-checkItem{display:flex;align-items:center;gap:10px;padding:12px 14px;border-radius:14px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03)}
    .gsp-checkItem input{accent-color:#8b5cf6}
    .gsp-submit{border:1px solid rgba(109,184,255,.34);background:linear-gradient(135deg,rgba(48,216,255,.18),rgba(139,92,246,.30));color:rgba(246,250,255,.98);border-radius:14px;padding:14px 18px;font-size:1rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;gap:8px}
    .gsp-submit:disabled{opacity:.55;cursor:not-allowed}
    .gsp-state{display:flex;flex-direction:column;gap:16px;align-items:flex-start}
    .gsp-stateIcon{width:52px;height:52px;border-radius:16px;display:grid;place-items:center;background:rgba(25,164,122,.18);color:rgba(182,245,223,.98);font-size:1.4rem}
    @media (max-width:760px){.gsp-shell{padding:12px}.gsp-header,.gsp-body{padding-left:18px;padding-right:18px}.gsp-grid{grid-template-columns:1fr}}
  `;

  const renderQuestion = (question) => {
    const type = String(question.question_type || "").toLowerCase();
    const answerValue = answers[question.id];
    const options = Array.isArray(question.options_json?.choices) ? question.options_json.choices : [];

    if (type === "text") {
      return (
        <textarea
          className="gsp-textarea"
          value={answerValue || ""}
          onChange={(event) => updateAnswer(question.id, event.target.value)}
          placeholder="Write your answer here"
        />
      );
    }

    if (type === "rating" || type === "nps") {
      return (
        <div className="gsp-scale">
          {buildScaleRange(question).map((value) => (
            <button
              key={value}
              type="button"
              className={`gsp-scaleBtn ${Number(answerValue) === value ? "gsp-scaleBtn--active" : ""}`}
              onClick={() => updateAnswer(question.id, value)}
            >
              {value}
            </button>
          ))}
        </div>
      );
    }

    if (type === "yes_no") {
      return (
        <div className="gsp-choiceList">
          {["yes", "no"].map((value) => (
            <button
              key={value}
              type="button"
              className={`gsp-choiceBtn ${answerValue === value ? "gsp-choiceBtn--active" : ""}`}
              onClick={() => updateAnswer(question.id, value)}
            >
              {value === "yes" ? "Yes" : "No"}
            </button>
          ))}
        </div>
      );
    }

    if (type === "single_choice") {
      return (
        <div className="gsp-checkList">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className={`gsp-choiceBtn ${answerValue === option ? "gsp-choiceBtn--active" : ""}`}
              onClick={() => updateAnswer(question.id, option)}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    if (type === "multi_choice") {
      const currentValues = Array.isArray(answerValue) ? answerValue : [];
      return (
        <div className="gsp-checkList">
          {options.map((option) => (
            <label key={option} className="gsp-checkItem">
              <input
                type="checkbox"
                checked={currentValues.includes(option)}
                onChange={() => toggleMultiChoice(question.id, option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="gsp-shell">
        <style>{styles}</style>
        <div className="gsp-card">
          <div className="gsp-body" style={{ alignItems: "center", justifyContent: "center", minHeight: 260 }}>
            <FiLoader style={{ animation: "gsp-spin 1s linear infinite" }} />
            <div>Loading survey...</div>
          </div>
        </div>
      </div>
    );
  }

  if (state.mode === "answered" || state.mode === "expired" || state.mode === "submitted" || state.mode === "error") {
    const isSuccess = state.mode === "answered" || state.mode === "submitted";
    return (
      <div className="gsp-shell">
        <style>{styles}</style>
        <div className="gsp-card">
          <div className="gsp-header">
            <h1 className="gsp-title">{survey?.template?.name || "Guest Feedback Survey"}</h1>
            <p className="gsp-subtitle">
              {isSuccess
                ? state.thankYouMessage || "Thank you for sharing your feedback with the hotel."
                : error}
            </p>
          </div>
          <div className="gsp-body">
            <div className="gsp-state">
              {isSuccess ? <div className="gsp-stateIcon"><FiCheckCircle /></div> : <div className="gsp-stateIcon" style={{ background: "rgba(177,48,69,.22)", color: "rgba(255,214,214,.98)" }}><FiMessageSquare /></div>}
              {isSuccess ? null : <div className="gsp-banner gsp-banner--error">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gsp-shell">
      <style>{styles}</style>
      <div className="gsp-card">
        <div className="gsp-header">
          <h1 className="gsp-title">{survey?.template?.name || "Guest Feedback Survey"}</h1>
          <p className="gsp-subtitle">
            {survey?.template?.email_intro_text || "Thank you for your stay. Your feedback helps the hotel improve the guest experience."}
          </p>
        </div>
        <div className="gsp-body">
          {error ? <div className="gsp-banner gsp-banner--error">{error}</div> : null}
          <div className="gsp-grid">
            {!survey?.sendout?.guest_name ? (
              <label className="gsp-field">
                <span className="gsp-label">Your name</span>
                <input className="gsp-input" type="text" value={guestName} onChange={(event) => setGuestName(event.target.value)} placeholder="Optional" />
              </label>
            ) : (
              <div className="gsp-banner">Guest: <strong>{survey.sendout.guest_name}</strong></div>
            )}
            {!survey?.sendout?.guest_email ? (
              <label className="gsp-field">
                <span className="gsp-label">Your email</span>
                <input className="gsp-input" type="email" value={guestEmail} onChange={(event) => setGuestEmail(event.target.value)} placeholder="Optional" />
              </label>
            ) : (
              <div className="gsp-banner"><FiMail style={{ marginRight: 8 }} />{survey.sendout.guest_email}</div>
            )}
          </div>

          <form className="gsp-form" onSubmit={submitSurvey}>
            {questions.map((question) => (
              <article key={question.id} className="gsp-question">
                <div className="gsp-questionTitle">
                  {question.question_text}
                  {question.is_required ? <span className="gsp-required">Required</span> : null}
                </div>
                <div className="gsp-questionHelp">
                  {String(question.question_type || "").replace(/_/g, " ")}
                </div>
                {renderQuestion(question)}
              </article>
            ))}

            <button type="submit" className="gsp-submit" disabled={submitting}>
              {submitting ? <FiLoader style={{ animation: "gsp-spin 1s linear infinite" }} /> : <FiCheckCircle />}
              {submitting ? "Sending feedback..." : "Send feedback"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default GuestFeedbackSurveyPublicPage;
